import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, BarChart3 } from 'lucide-react';

import {
  ORDER_TYPE_LABELS,
  PICK_TYPE_LABELS,
  getCustomerProfitability,
  type CustomerProfitabilityRow,
  type ShipedgeOrderType,
  type ShipedgePickType,
} from '@/api/shipedgeReports.api';
import { Card } from '@/components/ui/Card';
import { ShipedgeAccessDenied, ShipedgePageHeader } from './_chrome';
import { useShipedgeAdminGate } from './_gate';

/**
 * Customer Profitability page. Phase 15 Day 7.
 *
 * Per-client P&L for a single calendar month, with two orthogonal
 * toggle filters:
 *   - Pick type (DTC / Wholesale / Distributor / Internal)
 *   - Order type (Fulfillment / Private Label)
 *
 * Filters are multi-select chips — empty means "all of this dimension."
 * The footer row sums the visible columns (parseFloat at the boundary;
 * the underlying server aggregations are Decimal-safe per ADR-022).
 */

const PICK_TYPE_OPTIONS: ShipedgePickType[] = ['batch', 'wholesale', 'distributor', 'internal'];
const ORDER_TYPE_OPTIONS: ShipedgeOrderType[] = ['fulfillment', 'private_label'];

export function CustomerProfitabilityPage() {
  const { canAccess } = useShipedgeAdminGate();

  const [month, setMonth] = useState(currentYearMonth);
  const [pickTypes, setPickTypes] = useState<ShipedgePickType[]>([]);
  const [orderTypes, setOrderTypes] = useState<ShipedgeOrderType[]>([]);

  // Guard: an empty/partial month input (user clearing the picker)
  // must not fire a request the server will 400. Tier 4 polish.
  const monthValid = /^\d{4}-\d{2}$/.test(month);

  const query = useQuery({
    queryKey: ['shipedge-customer-profitability', month, pickTypes, orderTypes],
    queryFn: () => getCustomerProfitability({ month, pickTypes, orderTypes }),
    enabled: canAccess && monthValid,
  });

  const totals = useMemo(() => sumTotals(query.data ?? []), [query.data]);

  if (!canAccess) return <ShipedgeAccessDenied />;

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <ShipedgePageHeader
        title="Customer Profitability"
        description="Per-client P&L by month — markup, handling, box-cost burden, total billed."
      />

      <div className="flex flex-wrap items-end gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-kw-muted">Month</span>
          <input
            type="month"
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
            }}
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </label>
        <ChipGroup
          label="Pick type"
          options={PICK_TYPE_OPTIONS.map((v) => ({ value: v, label: PICK_TYPE_LABELS[v] }))}
          selected={pickTypes}
          onChange={setPickTypes}
        />
        <ChipGroup
          label="Order type"
          options={ORDER_TYPE_OPTIONS.map((v) => ({ value: v, label: ORDER_TYPE_LABELS[v] }))}
          selected={orderTypes}
          onChange={setOrderTypes}
        />
      </div>

      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : query.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load report</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {query.error instanceof Error ? query.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : (query.data ?? []).length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <BarChart3 className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No shipments match these filters</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Try a different month, or clear the filter chips above. Data lands the morning after a sync runs
              (cron at 02:00 ET); use Sync Log to verify.
            </p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-x-auto p-0">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="border-b border-kw-border bg-kw-bg/40 text-left text-[11px] uppercase tracking-wide text-kw-muted">
                <th className="px-4 py-2">Client</th>
                <th className="px-4 py-2 text-right">Shipments</th>
                <th className="px-4 py-2 text-right">Units</th>
                <th className="px-4 py-2 text-right">Markup</th>
                <th className="px-4 py-2 text-right">Handling</th>
                <th className="px-4 py-2 text-right">Box cost</th>
                <th className="px-4 py-2 text-right">Total billed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kw-border">
              {(query.data ?? []).map((row) => (
                <CustomerRow key={row.clientNormalizedKey} row={row} />
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-kw-border bg-kw-bg/30 font-medium text-kw-shark">
                <td className="px-4 py-2">Total ({(query.data ?? []).length} clients)</td>
                <td className="px-4 py-2 text-right">{totals.shipmentCount.toLocaleString()}</td>
                <td className="px-4 py-2 text-right">{totals.unitsShipped.toLocaleString()}</td>
                <td className="px-4 py-2 text-right">{usd(totals.markupRevenue)}</td>
                <td className="px-4 py-2 text-right">{usd(totals.handlingFeeRevenue)}</td>
                <td className="px-4 py-2 text-right">{usd(totals.boxCostBurden)}</td>
                <td className="px-4 py-2 text-right">{usd(totals.totalBilled)}</td>
              </tr>
            </tfoot>
          </table>
        </Card>
      )}
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────

function CustomerRow({ row }: { row: CustomerProfitabilityRow }) {
  return (
    <tr className="text-kw-shark hover:bg-kw-bg/30">
      <td className="px-4 py-2 font-medium">{row.client}</td>
      <td className="px-4 py-2 text-right tabular-nums">{row.shipmentCount.toLocaleString()}</td>
      <td className="px-4 py-2 text-right tabular-nums">{row.unitsShipped.toLocaleString()}</td>
      <td className="px-4 py-2 text-right tabular-nums">{usd(row.markupRevenue)}</td>
      <td className="px-4 py-2 text-right tabular-nums">{usd(row.handlingFeeRevenue)}</td>
      <td className="px-4 py-2 text-right tabular-nums text-kw-muted">{usd(row.boxCostBurden)}</td>
      <td className="px-4 py-2 text-right font-medium tabular-nums">{usd(row.totalBilled)}</td>
    </tr>
  );
}

function ChipGroup<T extends string>({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  selected: T[];
  onChange: (next: T[]) => void;
}) {
  function toggle(value: T): void {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  }

  return (
    // fieldset/legend associates the group label programmatically;
    // aria-pressed exposes the toggle state beyond color (Tier 4 a11y).
    <fieldset className="flex flex-col gap-1 border-0 p-0">
      <legend className="text-[12px] font-medium text-kw-muted">{label}</legend>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => {
          const active = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              onClick={() => {
                toggle(opt.value);
              }}
              className={`rounded-full px-2.5 py-1 text-[12px] transition ${
                active
                  ? 'bg-kw-cerulean text-white'
                  : 'border border-kw-border bg-white text-kw-shark hover:border-kw-cerulean'
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

// ─── Pure helpers ───────────────────────────────────────────────────────

function currentYearMonth(): string {
  const now = new Date();
  return `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

interface ProfitabilityTotals {
  shipmentCount: number;
  unitsShipped: number;
  markupRevenue: number;
  handlingFeeRevenue: number;
  boxCostBurden: number;
  totalBilled: number;
}

function sumTotals(rows: CustomerProfitabilityRow[]): ProfitabilityTotals {
  return rows.reduce<ProfitabilityTotals>(
    (acc, row) => ({
      shipmentCount: acc.shipmentCount + row.shipmentCount,
      unitsShipped: acc.unitsShipped + row.unitsShipped,
      markupRevenue: acc.markupRevenue + parseFloat(row.markupRevenue),
      handlingFeeRevenue: acc.handlingFeeRevenue + parseFloat(row.handlingFeeRevenue),
      boxCostBurden: acc.boxCostBurden + parseFloat(row.boxCostBurden),
      totalBilled: acc.totalBilled + parseFloat(row.totalBilled),
    }),
    {
      shipmentCount: 0,
      unitsShipped: 0,
      markupRevenue: 0,
      handlingFeeRevenue: 0,
      boxCostBurden: 0,
      totalBilled: 0,
    },
  );
}

function usd(value: number | string): string {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}
